<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const ICON_OPTIONS = [
  { value: 'link', label: 'Link', icon: 'i-lucide-link' },
  { value: 'star', label: 'Star', icon: 'i-lucide-star' },
  { value: 'heart', label: 'Heart', icon: 'i-lucide-heart' },
  { value: 'zap', label: 'Zap', icon: 'i-lucide-zap' },
  { value: 'briefcase', label: 'Briefcase', icon: 'i-lucide-briefcase' },
  { value: 'chart', label: 'Chart', icon: 'i-lucide-bar-chart-2' },
  { value: 'phone', label: 'Phone', icon: 'i-lucide-phone' },
  { value: 'mail', label: 'Mail', icon: 'i-lucide-mail' },
  { value: 'message', label: 'Message', icon: 'i-lucide-message-circle' },
  { value: 'bell', label: 'Bell', icon: 'i-lucide-bell' },
  { value: 'wrench', label: 'Wrench', icon: 'i-lucide-wrench' },
  { value: 'settings', label: 'Settings', icon: 'i-lucide-settings' },
  { value: 'database', label: 'Database', icon: 'i-lucide-database' },
  { value: 'users', label: 'Users', icon: 'i-lucide-users' },
  { value: 'globe', label: 'Globe', icon: 'i-lucide-globe' },
  { value: 'calendar', label: 'Calendar', icon: 'i-lucide-calendar' },
  { value: 'file-text', label: 'File', icon: 'i-lucide-file-text' },
  { value: 'layout-dashboard', label: 'Layout', icon: 'i-lucide-layout-dashboard' },
  { value: 'shopping-bag', label: 'Shopping', icon: 'i-lucide-shopping-bag' },
  { value: 'flag', label: 'Flag', icon: 'i-lucide-flag' },
  { value: 'map', label: 'Map', icon: 'i-lucide-map' },
  { value: 'video', label: 'Video', icon: 'i-lucide-video' },
  { value: 'shield', label: 'Shield', icon: 'i-lucide-shield' },
  { value: 'rocket', label: 'Rocket', icon: 'i-lucide-rocket' },
  { value: 'book', label: 'Book', icon: 'i-lucide-book' },
  { value: 'cloud', label: 'Cloud', icon: 'i-lucide-cloud' },
];

export default {
  components: {
    NextButton,
    Checkbox,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      showIconDropdown: false,
      iconOptions: ICON_OPTIONS,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
        icon: 'link',
        show_in_navigation: false,
        only_admin: false,
      },
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
    selectedIconOption() {
      return this.iconOptions.find(opt => opt.value === this.app.icon) || this.iconOptions[0];
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.icon = this.selectedAppData.icon || 'link';
      this.app.show_in_navigation = !!this.selectedAppData.show_in_navigation;
      this.app.only_admin = !!this.selectedAppData.only_admin;
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
        icon: 'link',
        show_in_navigation: false,
        only_admin: false,
      };
      this.$emit('close');
    },
    selectIcon(option) {
      this.app.icon = option.value;
      this.showIconDropdown = false;
    },
    toggleIconDropdown() {
      this.showIconDropdown = !this.showIconDropdown;
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          icon: this.app.icon,
          show_in_navigation: !!this.app.show_in_navigation,
          only_admin: !!this.app.only_admin,
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />

        <!-- Icon Selector -->
        <div class="mb-4">
          <label class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
            Ícone do menu lateral
          </label>
          <div class="relative">
            <button
              type="button"
              class="flex items-center justify-between w-full px-3 py-2 text-left border rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-woot-500 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @click="toggleIconDropdown"
            >
              <span class="flex items-center gap-2">
                <span :class="selectedIconOption.icon" class="size-4" />
                <span>{{ selectedIconOption.label }}</span>
              </span>
              <span :class="showIconDropdown ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-4" />
            </button>
            <div
              v-if="showIconDropdown"
              class="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-lg shadow-lg dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-h-48"
            >
              <button
                v-for="option in iconOptions"
                :key="option.value"
                type="button"
                class="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700"
                :class="{ 'bg-woot-50 dark:bg-woot-900/20 text-woot-600': option.value === app.icon }"
                @click="selectIcon(option)"
              >
                <span :class="option.icon" class="size-4" />
                <span>{{ option.label }}</span>
                <span v-if="option.value === app.icon" class="ml-auto i-lucide-check size-4 text-woot-600" />
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center mt-4 mb-2">
          <Checkbox v-model="app.show_in_navigation" />
          <div class="ml-2">
            <label class="cursor-pointer select-none text-sm font-medium text-slate-700 dark:text-slate-200">
              Adicionar ao menu lateral
            </label>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Marque para exibir este aplicativo no menu lateral principal. Se desmarcado, ele aparecerá como uma aba dentro das conversas.
            </p>
          </div>
        </div>

        <div class="flex items-center mt-4 mb-2">
          <Checkbox v-model="app.only_admin" />
          <div class="ml-2">
            <label class="cursor-pointer select-none text-sm font-medium text-slate-700 dark:text-slate-200">
              Somente administradores
            </label>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Marque para exibir este aplicativo apenas para usuários com permissão de administrador.
            </p>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>

